/**
 * French Student Notes - Expanded Study Materials
 * Comprehensive notes for Years 7-10 French as a Foreign Language
 * Based on Vanuatu National Syllabus - Aligned with frenchCurriculum.js
 * Last Updated: February 5, 2026
 */

// ============================================
// IMAGE RESOURCES FOR FRENCH NOTES
// ============================================
export const frenchImages = {
  eiffel: "https://images.unsplash.com/photo-1511739001486-6bfe10ce65f4?w=800",
  classroom: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800",
  cafe: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800",
  market: "https://images.unsplash.com/photo-1533900298318-6b8da08a523e?w=800",
  family: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800",
  food: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800",
  travel: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800",
  environment: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800"
};

// ============================================
// YEAR 7 FRENCH STUDENT NOTES
// Aligned with frenchCurriculum.js strands
// ============================================
export const year7FrenchStudentNotes = {
  strand1: {
    title: "Term 1: Se présenter (Introducing Yourself)",
    overview: "Learn to introduce yourself, talk about your family, languages, and school journey in French.",
    image: frenchImages.classroom,
    units: [
      {
        id: "1.1",
        title: "Se présenter - Prénom et âge",
        objectives: [
          "Introduce yourself using je m'appelle and j'ai ... ans",
          "Spell names using the French alphabet",
          "Count and use numbers 1-20",
          "Create an identity card in French"
        ],
        keyTerms: [
          { term: "Je m'appelle", definition: "My name is... (literally: I call myself)" },
          { term: "J'ai ... ans", definition: "I am ... years old (literally: I have ... years)" },
          { term: "L'alphabet", definition: "The alphabet - learn to spell names in French" },
          { term: "Les nombres", definition: "Numbers 1-20: un, deux, trois, quatre, cinq..." }
        ],
        studyNotes: `
## Se présenter (Introducing Yourself)

### Basic Greetings
| French | English |
|--------|---------|
| Bonjour | Hello / Good day |
| Salut | Hi (informal) |
| Comment tu t'appelles? | What's your name? |
| Je m'appelle... | My name is... |

### Telling Your Age
- **Question:** Tu as quel âge? (How old are you?)
- **Answer:** J'ai [number] ans. (I am [number] years old.)

### Numbers 1-20
| 1-10 | 11-20 |
|------|-------|
| un, deux, trois | onze, douze, treize |
| quatre, cinq, six | quatorze, quinze, seize |
| sept, huit, neuf, dix | dix-sept, dix-huit, dix-neuf, vingt |

### The French Alphabet
A (ah), B (bé), C (cé), D (dé), E (euh), F (ef), G (jé), H (ash), I (ee), J (ji), K (ka), L (el), M (em), N (en), O (oh), P (pé), Q (ku), R (air), S (ess), T (té), U (u), V (vé), W (dooblvé), X (eeks), Y (ee-grek), Z (zed)
        `,
        examTips: [
          "Always use 'J'ai' not 'Je suis' for age in French",
          "Practice spelling your name letter by letter",
          "Remember: numbers 17-19 are literally 'ten-seven', 'ten-eight', 'ten-nine'"
        ],
        practiceQuestions: [
          { question: "How do you say 'My name is Marie' in French?", answer: "Je m'appelle Marie" },
          { question: "How do you say 'I am 12 years old' in French?", answer: "J'ai douze ans" },
          { question: "Spell 'PAUL' using the French alphabet", answer: "P-A-U-L (pé-ah-u-el)" }
        ]
      },
      {
        id: "1.2",
        title: "Présenter sa famille et ses langues",
        objectives: [
          "Present family members in French",
          "Use possessive adjectives (mon, ma, mes)",
          "Talk about languages spoken",
          "Understand multilingualism in Vanuatu"
        ],
        keyTerms: [
          { term: "La famille", definition: "The family" },
          { term: "Mon père / Ma mère", definition: "My father / My mother" },
          { term: "Mon frère / Ma sœur", definition: "My brother / My sister" },
          { term: "Je parle...", definition: "I speak..." }
        ],
        studyNotes: `
## La Famille (The Family)

### Family Vocabulary
| French | English |
|--------|---------|
| le père | father |
| la mère | mother |
| le frère | brother |
| la sœur | sister |
| le grand-père | grandfather |
| la grand-mère | grandmother |

### Possessive Adjectives
| Masculine | Feminine | Plural |
|-----------|----------|--------|
| mon (my) | ma (my) | mes (my) |
| ton (your) | ta (your) | tes (your) |
| son (his/her) | sa (his/her) | ses (his/her) |

### Languages
- Je parle **français** (I speak French)
- Je parle **anglais** (I speak English)
- Je parle **bislama** (I speak Bislama)

### Vanuatu Context
Vanuatu is multilingual! Many students speak:
- Their local language (langue maternelle)
- Bislama (national language)
- French and/or English (official languages)
        `,
        examTips: [
          "Use 'mon' before masculine nouns, 'ma' before feminine nouns",
          "Exception: Use 'mon' before feminine nouns starting with a vowel (mon amie)",
          "Remember the difference between 'parler' (speak) and 'langue' (language)"
        ],
        practiceQuestions: [
          { question: "How do you say 'my mother' in French?", answer: "ma mère" },
          { question: "Complete: Je parle français et ___", answer: "anglais/bislama" },
          { question: "What is 'my brother' in French?", answer: "mon frère" }
        ]
      }
    ]
  },
  strand2: {
    title: "Term 2: Le français et la francophonie",
    overview: "Explore the French-speaking world, school supplies, sports, and music vocabulary.",
    image: frenchImages.eiffel,
    units: [
      {
        id: "2.1",
        title: "La francophonie mondiale",
        objectives: [
          "Identify Francophone countries on a map",
          "Understand French cultural diversity",
          "Learn about French in Vanuatu's history"
        ],
        keyTerms: [
          { term: "La francophonie", definition: "The French-speaking world - countries where French is spoken" },
          { term: "Un pays francophone", definition: "A French-speaking country" },
          { term: "La France", definition: "France - the origin country of French" },
          { term: "Le Vanuatu", definition: "Vanuatu - a bilingual nation (French & English)" }
        ],
        studyNotes: `
## La Francophonie (The French-Speaking World)

### Major Francophone Regions
1. **Europe:** France, Belgium, Switzerland, Luxembourg
2. **Africa:** Senegal, Côte d'Ivoire, Cameroon, Madagascar
3. **North America:** Quebec (Canada), Haiti
4. **Pacific:** Vanuatu, New Caledonia, French Polynesia

### French in Vanuatu
- French arrived during the colonial period (Condominium with Britain)
- Vanuatu is the only Pacific nation member of La Francophonie
- About 30% of ni-Vanuatu speak French

### Useful Expressions
- D'où viens-tu? (Where are you from?)
- Je viens du Vanuatu (I come from Vanuatu)
- C'est un pays francophone (It's a French-speaking country)
        `,
        examTips: [
          "Know at least 5 Francophone countries from different continents",
          "Remember Vanuatu's unique position as a bilingual nation",
          "Use 'de' + country name to say where you're from"
        ],
        practiceQuestions: [
          { question: "Name 3 Francophone countries in Africa", answer: "Senegal, Côte d'Ivoire, Cameroon (or others)" },
          { question: "Is Vanuatu a Francophone country?", answer: "Yes, it's a bilingual country (French and English)" },
          { question: "What does 'la francophonie' mean?", answer: "The French-speaking world" }
        ]
      },
      {
        id: "2.2",
        title: "Fournitures scolaires et sports",
        objectives: [
          "Name school supplies in French",
          "Learn sports vocabulary",
          "Express preferences using 'j'aime' and 'je préfère'"
        ],
        keyTerms: [
          { term: "Les fournitures scolaires", definition: "School supplies" },
          { term: "Un cahier / Un stylo", definition: "A notebook / A pen" },
          { term: "Le sport", definition: "Sport" },
          { term: "J'aime / Je préfère", definition: "I like / I prefer" }
        ],
        studyNotes: `
## Fournitures Scolaires (School Supplies)

### Essential School Items
| French | English |
|--------|---------|
| un cahier | a notebook |
| un stylo | a pen |
| un crayon | a pencil |
| une gomme | an eraser |
| un livre | a book |
| un sac | a bag |
| une règle | a ruler |

### Sports Vocabulary
| French | English |
|--------|---------|
| le football | soccer |
| le rugby | rugby |
| le volleyball | volleyball |
| la natation | swimming |
| le basketball | basketball |

### Expressing Preferences
- J'aime le football (I like soccer)
- Je préfère le rugby (I prefer rugby)
- Je n'aime pas la natation (I don't like swimming)
        `,
        examTips: [
          "Remember the gender of school items (un = masculine, une = feminine)",
          "Use 'le/la' before sports when expressing preferences",
          "To make negative: add 'ne...pas' around the verb"
        ],
        practiceQuestions: [
          { question: "What is 'a pen' in French?", answer: "un stylo" },
          { question: "How do you say 'I like soccer'?", answer: "J'aime le football" },
          { question: "What is 'une gomme'?", answer: "an eraser" }
        ]
      }
    ]
  },
  strand3: {
    title: "Term 3: La vie au collège",
    overview: "Learn to tell time, discuss school schedules, and explore arts and music.",
    image: frenchImages.classroom,
    units: [
      {
        id: "3.1",
        title: "L'heure et l'emploi du temps",
        objectives: [
          "Tell time in French",
          "Discuss school timetables",
          "Use time expressions in conversation"
        ],
        keyTerms: [
          { term: "Quelle heure est-il?", definition: "What time is it?" },
          { term: "Il est... heure(s)", definition: "It is... o'clock" },
          { term: "L'emploi du temps", definition: "The timetable/schedule" },
          { term: "Les jours de la semaine", definition: "Days of the week" }
        ],
        studyNotes: `
## L'Heure (Telling Time)

### Basic Time Expressions
- **Il est une heure** - It is 1 o'clock
- **Il est deux heures** - It is 2 o'clock
- **Il est midi** - It is noon
- **Il est minuit** - It is midnight

### Minutes
- et quart (quarter past) - 8h15 = huit heures et quart
- et demie (half past) - 8h30 = huit heures et demie
- moins le quart (quarter to) - 8h45 = neuf heures moins le quart

### Days of the Week
lundi, mardi, mercredi, jeudi, vendredi, samedi, dimanche

### Timetable Vocabulary
| French | English |
|--------|---------|
| le cours | the class/lesson |
| la récréation | recess/break |
| le déjeuner | lunch |
| la matière | subject |
        `,
        examTips: [
          "Use 'heure' (singular) for 1 o'clock, 'heures' (plural) for other times",
          "Days of the week are NOT capitalized in French",
          "Use 'moins' (minus) for times after half past"
        ],
        practiceQuestions: [
          { question: "How do you say 3:30 in French?", answer: "Il est trois heures et demie" },
          { question: "What is 'Wednesday' in French?", answer: "mercredi" },
          { question: "What does 'l'emploi du temps' mean?", answer: "the timetable/schedule" }
        ]
      },
      {
        id: "3.2",
        title: "Arts visuels et musique",
        objectives: [
          "Learn visual art vocabulary",
          "Discuss French music and artists",
          "Express artistic preferences"
        ],
        keyTerms: [
          { term: "L'art", definition: "Art" },
          { term: "La musique", definition: "Music" },
          { term: "Un artiste", definition: "An artist" },
          { term: "Une chanson", definition: "A song" }
        ],
        studyNotes: `
## Les Arts et la Musique

### Visual Art Vocabulary
| French | English |
|--------|---------|
| un tableau | a painting |
| un dessin | a drawing |
| les couleurs | colors |
| peindre | to paint |
| dessiner | to draw |

### Music Vocabulary
| French | English |
|--------|---------|
| une chanson | a song |
| un chanteur/une chanteuse | a singer |
| un instrument | an instrument |
| écouter | to listen |

### Expressing Opinions about Art
- C'est beau! (It's beautiful!)
- C'est intéressant (It's interesting)
- J'aime cette chanson (I like this song)
- Je préfère la musique traditionnelle (I prefer traditional music)
        `,
        examTips: [
          "Use 'ce/cette' (this) to point to specific artworks",
          "Remember: 'beau' (masc) vs 'belle' (fem) for beautiful",
          "Connect Vanuatu traditional arts to French descriptions"
        ],
        practiceQuestions: [
          { question: "What is 'a song' in French?", answer: "une chanson" },
          { question: "How do you say 'It's beautiful'?", answer: "C'est beau / C'est belle" },
          { question: "What does 'dessiner' mean?", answer: "to draw" }
        ]
      }
    ]
  },
  strand4: {
    title: "Term 4: Projets et révision",
    overview: "Complete cultural projects and review all Year 7 French content.",
    image: frenchImages.eiffel,
    units: [
      {
        id: "4.1",
        title: "Projet culturel",
        objectives: [
          "Plan and execute a cultural project",
          "Research using French sources",
          "Present findings in French"
        ],
        keyTerms: [
          { term: "Un projet", definition: "A project" },
          { term: "La recherche", definition: "Research" },
          { term: "Une présentation", definition: "A presentation" },
          { term: "La culture", definition: "Culture" }
        ],
        studyNotes: `
## Projet Culturel (Cultural Project)

### Project Ideas
1. **Mon pays francophone** - Research a French-speaking country
2. **Ma famille** - Create a family tree with descriptions
3. **Mon école** - Describe your school in French
4. **Les traditions** - Compare Vanuatu and French traditions

### Presentation Phrases
- Je vais présenter... (I am going to present...)
- Mon projet est sur... (My project is about...)
- Voici... (Here is...)
- En conclusion... (In conclusion...)

### Research Tips
- Use simple French websites
- Look for images to support your presentation
- Practice pronunciation before presenting
        `,
        examTips: [
          "Practice your presentation multiple times",
          "Use visual aids to help explain",
          "Speak slowly and clearly"
        ],
        practiceQuestions: [
          { question: "How do you start a presentation in French?", answer: "Je vais présenter..." },
          { question: "What does 'la recherche' mean?", answer: "research" },
          { question: "How do you say 'In conclusion'?", answer: "En conclusion" }
        ]
      },
      {
        id: "4.2",
        title: "Révision annuelle",
        objectives: [
          "Review all Year 7 vocabulary",
          "Practice grammar structures",
          "Prepare for Year 8 French"
        ],
        keyTerms: [
          { term: "La révision", definition: "Review/revision" },
          { term: "Le vocabulaire", definition: "Vocabulary" },
          { term: "La grammaire", definition: "Grammar" },
          { term: "L'examen", definition: "The exam" }
        ],
        studyNotes: `
## Révision Year 7 (Year 7 Review)

### Key Topics Covered
1. **Introductions:** Je m'appelle, J'ai ... ans
2. **Family:** père, mère, frère, sœur
3. **Languages:** Je parle français, anglais, bislama
4. **School:** fournitures, emploi du temps
5. **Time:** Quelle heure est-il?
6. **Francophonie:** French-speaking countries

### Grammar Review
- Articles: le, la, l', les, un, une, des
- Possessives: mon, ma, mes
- Present tense verbs: je suis, j'ai, je parle
- Negation: ne...pas

### Exam Preparation
- Review vocabulary lists
- Practice writing short paragraphs
- Listen to French audio
- Speak French with classmates
        `,
        examTips: [
          "Make flashcards for vocabulary review",
          "Practice writing about yourself in French",
          "Don't forget gender agreement (le/la)"
        ],
        practiceQuestions: [
          { question: "Write 3 sentences about yourself in French", answer: "Je m'appelle [name]. J'ai [age] ans. Je parle français." },
          { question: "What are the definite articles in French?", answer: "le, la, l', les" },
          { question: "How do you make a sentence negative?", answer: "Add ne...pas around the verb" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 8 FRENCH STUDENT NOTES
// ============================================
export const year8FrenchStudentNotes = {
  strand1: {
    title: "Term 1: Le français et nous",
    overview: "Review Year 7, explore French in Vanuatu, and develop learning strategies.",
    image: frenchImages.classroom,
    units: [
      {
        id: "1.1",
        title: "La rentrée et communication",
        objectives: [
          "Review Year 7 content",
          "Use classroom language effectively",
          "Compare school systems in Vanuatu and France"
        ],
        keyTerms: [
          { term: "La rentrée", definition: "Back to school / start of school year" },
          { term: "En classe", definition: "In class" },
          { term: "Répétez", definition: "Repeat (command)" },
          { term: "Je ne comprends pas", definition: "I don't understand" }
        ],
        studyNotes: `
## La Rentrée (Back to School)

### Classroom Instructions
| French | English |
|--------|---------|
| Écoutez | Listen |
| Répétez | Repeat |
| Lisez | Read |
| Écrivez | Write |
| Ouvrez vos livres | Open your books |
| Fermez vos cahiers | Close your notebooks |

### Asking for Help
- Je ne comprends pas (I don't understand)
- Pouvez-vous répéter? (Can you repeat?)
- Comment dit-on... en français? (How do you say... in French?)
- Qu'est-ce que ça veut dire? (What does that mean?)

### School in France vs Vanuatu
| France | Vanuatu |
|--------|---------|
| 4 terms | 4 terms |
| Starts September | Starts February |
| Long lunch break | Shorter lunch |
| Wednesday half-day | Full week |
        `,
        examTips: [
          "Learn classroom phrases to help you in class",
          "Use these phrases to participate more in French lessons",
          "Notice differences between French and local school systems"
        ],
        practiceQuestions: [
          { question: "How do you say 'I don't understand' in French?", answer: "Je ne comprends pas" },
          { question: "What does 'Répétez' mean?", answer: "Repeat" },
          { question: "When does school start in France?", answer: "September" }
        ]
      },
      {
        id: "1.2",
        title: "Descriptions physiques",
        objectives: [
          "Describe physical appearance",
          "Use adjective agreement correctly",
          "Form affirmative and negative sentences"
        ],
        keyTerms: [
          { term: "Grand/Grande", definition: "Tall (masc/fem)" },
          { term: "Petit/Petite", definition: "Short/small (masc/fem)" },
          { term: "Les cheveux", definition: "Hair" },
          { term: "Les yeux", definition: "Eyes" }
        ],
        studyNotes: `
## Descriptions Physiques (Physical Descriptions)

### Describing Height
- Il/Elle est grand(e) - He/She is tall
- Il/Elle est petit(e) - He/She is short
- Il/Elle est de taille moyenne - He/She is medium height

### Hair (Les cheveux)
| French | English |
|--------|---------|
| les cheveux noirs | black hair |
| les cheveux bruns | brown hair |
| les cheveux blonds | blonde hair |
| les cheveux longs | long hair |
| les cheveux courts | short hair |

### Eyes (Les yeux)
| French | English |
|--------|---------|
| les yeux marron | brown eyes |
| les yeux bleus | blue eyes |
| les yeux verts | green eyes |
| les yeux noirs | black eyes |

### Example Description
"Marie est grande. Elle a les cheveux longs et noirs. Elle a les yeux marron."
        `,
        examTips: [
          "Remember: 'cheveux' is always plural in French",
          "Adjectives agree with the noun they describe",
          "Use 'Il/Elle a' (has) for hair and eyes, not 'est'"
        ],
        practiceQuestions: [
          { question: "How do you say 'She has brown hair'?", answer: "Elle a les cheveux bruns" },
          { question: "What is the feminine of 'grand'?", answer: "grande" },
          { question: "Describe yourself in French (3 sentences)", answer: "Je suis [grand/petit]. J'ai les cheveux [color]. J'ai les yeux [color]." }
        ]
      }
    ]
  },
  strand2: {
    title: "Term 2: La vie dans la communauté",
    overview: "Learn about community life, food, traditions, and cultural customs.",
    image: frenchImages.market,
    units: [
      {
        id: "2.1",
        title: "Du jardin au marché",
        objectives: [
          "Learn garden and produce vocabulary",
          "Discuss food and cooking",
          "Plan events in French"
        ],
        keyTerms: [
          { term: "Le jardin", definition: "The garden" },
          { term: "Le marché", definition: "The market" },
          { term: "Les légumes", definition: "Vegetables" },
          { term: "Les fruits", definition: "Fruits" }
        ],
        studyNotes: `
## Du Jardin au Marché (From Garden to Market)

### Garden Vocabulary
| French | English |
|--------|---------|
| le jardin | garden |
| planter | to plant |
| arroser | to water |
| récolter | to harvest |

### Produce Vocabulary
| Légumes (Vegetables) | Fruits |
|---------------------|--------|
| les tomates | les bananes |
| les carottes | les mangues |
| le manioc | les papayes |
| le taro | les noix de coco |

### At the Market
- Combien ça coûte? (How much does it cost?)
- Je voudrais... (I would like...)
- C'est combien? (How much is it?)
- Un kilo de... (A kilo of...)
        `,
        examTips: [
          "Learn local Vanuatu produce names in French",
          "Practice market conversations",
          "Use 'du/de la/des' for quantities"
        ],
        practiceQuestions: [
          { question: "How do you ask 'How much does it cost?'", answer: "Combien ça coûte? / C'est combien?" },
          { question: "What is 'le manioc' in English?", answer: "cassava/manioc" },
          { question: "How do you say 'I would like bananas'?", answer: "Je voudrais des bananes" }
        ]
      },
      {
        id: "2.2",
        title: "Coutumes et traditions",
        objectives: [
          "Learn about Vanuatu customs in French",
          "Compare traditions from different cultures",
          "Create cultural presentations"
        ],
        keyTerms: [
          { term: "Une coutume", definition: "A custom" },
          { term: "Une tradition", definition: "A tradition" },
          { term: "Un mariage", definition: "A wedding" },
          { term: "L'artisanat", definition: "Crafts/handicrafts" }
        ],
        studyNotes: `
## Coutumes et Traditions

### Vanuatu Traditions in French
- La cérémonie kastom (custom ceremony)
- Le mariage traditionnel (traditional wedding)
- La danse traditionnelle (traditional dance)
- L'artisanat local (local crafts)

### Describing Traditions
- Au Vanuatu, on... (In Vanuatu, we/people...)
- C'est une tradition importante (It's an important tradition)
- Cette coutume représente... (This custom represents...)

### Comparing Cultures
- En France, on fête... (In France, people celebrate...)
- Au Vanuatu, c'est différent (In Vanuatu, it's different)
- Les deux pays ont... (Both countries have...)
        `,
        examTips: [
          "Use 'on' for general statements about what people do",
          "Be able to describe at least one Vanuatu tradition in French",
          "Compare and contrast using simple structures"
        ],
        practiceQuestions: [
          { question: "How do you say 'traditional wedding' in French?", answer: "le mariage traditionnel" },
          { question: "What does 'l'artisanat' mean?", answer: "crafts/handicrafts" },
          { question: "Start a sentence comparing Vanuatu and France", answer: "Au Vanuatu... mais en France..." }
        ]
      }
    ]
  },
  strand3: {
    title: "Term 3: Communauté et environnement",
    overview: "Discuss family life, tourism, environmental protection, and eco-citizenship.",
    image: frenchImages.environment,
    units: [
      {
        id: "3.1",
        title: "La vie en famille et le tourisme",
        objectives: [
          "Describe family life in detail",
          "Welcome French tourists",
          "Use past and future tenses"
        ],
        keyTerms: [
          { term: "La vie quotidienne", definition: "Daily life" },
          { term: "Le touriste", definition: "Tourist" },
          { term: "Bienvenue", definition: "Welcome" },
          { term: "Hier/Demain", definition: "Yesterday/Tomorrow" }
        ],
        studyNotes: `
## La Vie en Famille et le Tourisme

### Daily Life Vocabulary
- Se lever (to get up)
- Se coucher (to go to bed)
- Manger (to eat)
- Travailler (to work)
- Se reposer (to rest)

### Welcoming Tourists
- Bienvenue au Vanuatu! (Welcome to Vanuatu!)
- Comment puis-je vous aider? (How can I help you?)
- Voici notre village (Here is our village)
- Vous pouvez voir... (You can see...)

### Introduction to Past Tense
- Hier, j'ai mangé... (Yesterday, I ate...)
- J'ai visité... (I visited...)
- C'était beau! (It was beautiful!)

### Introduction to Future
- Demain, je vais... (Tomorrow, I'm going to...)
- Nous allons visiter... (We're going to visit...)
        `,
        examTips: [
          "Learn reflexive verbs for daily routine",
          "Practice tourist conversation scenarios",
          "Start using simple past and future expressions"
        ],
        practiceQuestions: [
          { question: "How do you say 'Welcome to Vanuatu'?", answer: "Bienvenue au Vanuatu!" },
          { question: "What does 'Hier' mean?", answer: "Yesterday" },
          { question: "How do you say 'I visited'?", answer: "J'ai visité" }
        ]
      },
      {
        id: "3.2",
        title: "Protection de l'environnement",
        objectives: [
          "Learn environmental vocabulary",
          "Discuss natural disasters",
          "Plan eco-citizen actions"
        ],
        keyTerms: [
          { term: "L'environnement", definition: "The environment" },
          { term: "Protéger", definition: "To protect" },
          { term: "Recycler", definition: "To recycle" },
          { term: "Un cyclone", definition: "A cyclone" }
        ],
        studyNotes: `
## Protection de l'Environnement

### Environmental Vocabulary
| French | English |
|--------|---------|
| l'environnement | environment |
| la nature | nature |
| la pollution | pollution |
| le réchauffement climatique | climate change |

### Natural Disasters in Vanuatu
- Un cyclone (a cyclone)
- Un séisme/tremblement de terre (an earthquake)
- Une éruption volcanique (a volcanic eruption)
- Une inondation (a flood)

### Eco-citizen Actions
- Recycler les déchets (Recycle waste)
- Économiser l'eau (Save water)
- Protéger la nature (Protect nature)
- Planter des arbres (Plant trees)

### Expressing Obligation
- Il faut + infinitive (We must...)
- Il faut protéger l'environnement
- Il faut recycler
        `,
        examTips: [
          "Learn vocabulary for Vanuatu's environmental challenges",
          "Use 'Il faut' to express what must be done",
          "Connect environmental topics to local context"
        ],
        practiceQuestions: [
          { question: "What is 'un cyclone' in English?", answer: "a cyclone" },
          { question: "How do you say 'We must protect the environment'?", answer: "Il faut protéger l'environnement" },
          { question: "Name 2 eco-citizen actions in French", answer: "Recycler, économiser l'eau, planter des arbres" }
        ]
      }
    ]
  },
  strand4: {
    title: "Term 4: Approfondissement et projets",
    overview: "Complete community projects and consolidate Year 8 learning.",
    image: frenchImages.family,
    units: [
      {
        id: "4.1",
        title: "Projet communautaire",
        objectives: [
          "Plan and execute a community project",
          "Conduct research in French",
          "Present to an audience"
        ],
        keyTerms: [
          { term: "La communauté", definition: "The community" },
          { term: "Un projet", definition: "A project" },
          { term: "Interviewer", definition: "To interview" },
          { term: "Présenter", definition: "To present" }
        ],
        studyNotes: `
## Projet Communautaire

### Project Ideas
1. **Mon village** - Describe your village/community
2. **Les métiers** - Interview people about their jobs
3. **L'environnement local** - Local environmental issues
4. **Les traditions** - Document local traditions

### Interview Techniques
- Bonjour, je m'appelle... (Hello, my name is...)
- Je fais un projet sur... (I'm doing a project on...)
- Puis-je vous poser des questions? (May I ask you questions?)
- Merci pour votre temps (Thank you for your time)

### Presentation Structure
1. Introduction: Je vais présenter...
2. Body: D'abord... Ensuite... Enfin...
3. Conclusion: En conclusion...
        `,
        examTips: [
          "Prepare your interview questions in advance",
          "Practice your presentation multiple times",
          "Use transition words: d'abord, ensuite, enfin"
        ],
        practiceQuestions: [
          { question: "How do you ask permission to ask questions?", answer: "Puis-je vous poser des questions?" },
          { question: "What transition word means 'finally'?", answer: "Enfin" },
          { question: "How do you thank someone for their time?", answer: "Merci pour votre temps" }
        ]
      },
      {
        id: "4.2",
        title: "Révision et consolidation",
        objectives: [
          "Review all Year 8 content",
          "Master key grammar structures",
          "Prepare for Year 9"
        ],
        keyTerms: [
          { term: "La révision", definition: "Review" },
          { term: "Consolider", definition: "To consolidate" },
          { term: "Maîtriser", definition: "To master" },
          { term: "Progresser", definition: "To progress" }
        ],
        studyNotes: `
## Révision Year 8

### Key Topics Covered
1. **Classroom language:** Instructions, asking for help
2. **Physical descriptions:** Height, hair, eyes
3. **Community:** Garden, market, food
4. **Traditions:** Customs, celebrations
5. **Tourism:** Welcoming visitors
6. **Environment:** Protection, eco-actions

### Grammar Review
- Adjective agreement (gender and number)
- Passé composé (j'ai + past participle)
- Future with 'aller' (je vais + infinitive)
- 'Il faut' + infinitive

### Self-Assessment
- Can I describe someone physically?
- Can I talk about my community?
- Can I discuss environmental issues?
- Can I use past and future tenses?
        `,
        examTips: [
          "Review vocabulary from each term",
          "Practice writing paragraphs on each topic",
          "Focus on areas where you need improvement"
        ],
        practiceQuestions: [
          { question: "Write a physical description of yourself", answer: "Je suis [grand/petit]. J'ai les cheveux [description]. J'ai les yeux [color]." },
          { question: "Name 3 things you learned about in Year 8", answer: "Community life, traditions, environment (or similar)" },
          { question: "What grammar tenses did you learn?", answer: "Passé composé (past), future with aller" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 9 FRENCH STUDENT NOTES
// ============================================
export const year9FrenchStudentNotes = {
  strand1: {
    title: "Term 1: La rentrée et les voyages",
    overview: "Back to school topics, eco-responsibility, and travel to Francophone countries.",
    image: frenchImages.travel,
    units: [
      {
        id: "1.1",
        title: "C'est la rentrée!",
        objectives: [
          "Review and expand Year 7-8 knowledge",
          "Discuss eco-responsible shopping",
          "Talk about school uniforms and routines"
        ],
        keyTerms: [
          { term: "La rentrée scolaire", definition: "Back to school" },
          { term: "Éco-responsable", definition: "Eco-responsible" },
          { term: "L'uniforme scolaire", definition: "School uniform" },
          { term: "Les fournitures", definition: "Supplies" }
        ],
        studyNotes: `
## C'est la rentrée! (Back to School!)

### School Supplies - Eco-responsible
- Acheter des fournitures recyclées (Buy recycled supplies)
- Réutiliser les cahiers (Reuse notebooks)
- Éviter le plastique (Avoid plastic)

### School Uniforms Discussion
- Je porte un uniforme (I wear a uniform)
- L'uniforme est pratique (The uniform is practical)
- Je préfère/Je n'aime pas l'uniforme (I prefer/don't like the uniform)

### Daily Routine Review
| Time | Activity |
|------|----------|
| 6h00 | Je me lève |
| 7h00 | Je prends le petit-déjeuner |
| 8h00 | Je vais à l'école |
| 15h00 | Je rentre à la maison |
        `,
        examTips: [
          "Be able to discuss your opinion on uniforms",
          "Know eco-responsible vocabulary",
          "Review daily routine expressions from Year 8"
        ],
        practiceQuestions: [
          { question: "What does 'éco-responsable' mean?", answer: "eco-responsible" },
          { question: "How do you say 'I wear a uniform'?", answer: "Je porte un uniforme" },
          { question: "Give one eco-responsible school tip", answer: "Acheter des fournitures recyclées / Réutiliser les cahiers" }
        ]
      },
      {
        id: "1.2",
        title: "Nous partons en voyage!",
        objectives: [
          "Learn advanced travel vocabulary",
          "Discuss Francophone countries in detail",
          "Express travel preferences and plans"
        ],
        keyTerms: [
          { term: "Un voyage", definition: "A trip/journey" },
          { term: "L'avion", definition: "The plane" },
          { term: "Un pays", definition: "A country" },
          { term: "Je voudrais visiter", definition: "I would like to visit" }
        ],
        studyNotes: `
## Les Voyages (Travel)

### Travel Vocabulary
| French | English |
|--------|---------|
| un voyage | a trip |
| l'aéroport | the airport |
| l'avion | the plane |
| le passeport | the passport |
| les bagages | luggage |
| une valise | a suitcase |

### Francophone Destinations
- La France - Paris, Nice, Lyon
- La Nouvelle-Calédonie - Nouméa
- La Polynésie française - Tahiti
- Le Canada - Québec, Montréal

### Expressing Travel Plans
- Je voudrais visiter... (I would like to visit...)
- J'aimerais aller à... (I would like to go to...)
- Un jour, je vais voyager en... (One day, I will travel to...)
        `,
        examTips: [
          "Know key Francophone destinations",
          "Use conditional (je voudrais) for polite requests",
          "Be able to describe your dream destination"
        ],
        practiceQuestions: [
          { question: "What is 'luggage' in French?", answer: "les bagages" },
          { question: "Name a French city you would like to visit", answer: "Paris, Nice, Lyon, etc." },
          { question: "How do you say 'I would like to visit France'?", answer: "Je voudrais visiter la France" }
        ]
      }
    ]
  },
  strand2: {
    title: "Term 2: Fêtes et traditions",
    overview: "Explore festivals, celebrations, and traditional events in French-speaking cultures.",
    image: frenchImages.cafe,
    units: [
      {
        id: "2.1",
        title: "Les fêtes",
        objectives: [
          "Learn vocabulary for celebrations",
          "Compare festivals in different cultures",
          "Write invitations in French"
        ],
        keyTerms: [
          { term: "Une fête", definition: "A party/celebration" },
          { term: "Un anniversaire", definition: "A birthday" },
          { term: "Célébrer", definition: "To celebrate" },
          { term: "Inviter", definition: "To invite" }
        ],
        studyNotes: `
## Les Fêtes (Celebrations)

### Celebration Vocabulary
| French | English |
|--------|---------|
| un anniversaire | birthday |
| Noël | Christmas |
| le Nouvel An | New Year |
| une fête nationale | national holiday |
| un mariage | wedding |

### French Celebrations
- Le 14 juillet - Fête nationale française (Bastille Day)
- La Toussaint - All Saints' Day
- Pâques - Easter

### Vanuatu Celebrations
- L'Indépendance - 30 juillet (Independence Day)
- Le Jour de la Constitution
- Les fêtes traditionnelles kastom

### Writing Invitations
- Je t'invite à... (I invite you to...)
- La fête aura lieu le... (The party will take place on...)
- Viens célébrer avec moi! (Come celebrate with me!)
        `,
        examTips: [
          "Know key celebration dates for France and Vanuatu",
          "Be able to write a simple invitation",
          "Compare celebration traditions"
        ],
        practiceQuestions: [
          { question: "When is French National Day?", answer: "Le 14 juillet (July 14th)" },
          { question: "When is Vanuatu Independence Day?", answer: "Le 30 juillet (July 30th)" },
          { question: "How do you say 'I invite you'?", answer: "Je t'invite" }
        ]
      },
      {
        id: "2.2",
        title: "Mode et vêtements",
        objectives: [
          "Learn clothing vocabulary",
          "Describe what people wear",
          "Discuss fashion preferences"
        ],
        keyTerms: [
          { term: "Les vêtements", definition: "Clothes" },
          { term: "Porter", definition: "To wear" },
          { term: "La mode", definition: "Fashion" },
          { term: "Une robe", definition: "A dress" }
        ],
        studyNotes: `
## Les Vêtements (Clothing)

### Clothing Vocabulary
| French | English |
|--------|---------|
| une chemise | a shirt |
| un pantalon | trousers |
| une jupe | a skirt |
| une robe | a dress |
| des chaussures | shoes |
| un chapeau | a hat |

### Colors Review
| French | English |
|--------|---------|
| rouge | red |
| bleu | blue |
| vert | green |
| jaune | yellow |
| noir | black |
| blanc | white |

### Describing Outfits
- Je porte une chemise bleue (I'm wearing a blue shirt)
- Elle porte une robe rouge (She's wearing a red dress)
- Il porte un pantalon noir (He's wearing black trousers)

### Traditional Clothing
- La robe mission (traditional island dress)
- Le sulu (traditional wrap)
        `,
        examTips: [
          "Remember adjective placement (usually after noun)",
          "Colors agree with the noun they describe",
          "Be able to describe your own outfit"
        ],
        practiceQuestions: [
          { question: "How do you say 'a blue shirt'?", answer: "une chemise bleue" },
          { question: "What is 'shoes' in French?", answer: "des chaussures" },
          { question: "Describe what you're wearing", answer: "Je porte..." }
        ]
      }
    ]
  },
  strand3: {
    title: "Term 3: Histoire et culture",
    overview: "Learn about heroes, famous personalities, and world traditions.",
    image: frenchImages.eiffel,
    units: [
      {
        id: "3.1",
        title: "Ils ont écrit l'histoire",
        objectives: [
          "Learn about famous Francophone figures",
          "Write biographical texts",
          "Use the simple future tense"
        ],
        keyTerms: [
          { term: "Un héros", definition: "A hero" },
          { term: "Célèbre", definition: "Famous" },
          { term: "Une biographie", definition: "A biography" },
          { term: "Le futur simple", definition: "Simple future tense" }
        ],
        studyNotes: `
## Héros et Personnalités

### Famous Francophone Figures
- **Father Walter Lini** - Premier ministre du Vanuatu
- **Marie Curie** - Scientific française célèbre
- **Léopold Sédar Senghor** - Poète et président sénégalais

### Vanuatu Personalities
- Walter Lini - Father of the Nation
- Chiefs and traditional leaders
- Modern Ni-Vanuatu achievers

### Simple Future Tense
Formation: verb stem + future endings
| Subject | Ending | Example |
|---------|--------|---------|
| je | -ai | je parlerai |
| tu | -as | tu parleras |
| il/elle | -a | il parlera |
| nous | -ons | nous parlerons |
| vous | -ez | vous parlerez |
| ils/elles | -ont | ils parleront |

### Writing a Biography
- Il/Elle est né(e) en... (He/She was born in...)
- Il/Elle a étudié... (He/She studied...)
- Il/Elle est devenu(e) célèbre... (He/She became famous...)
        `,
        examTips: [
          "Know at least 2-3 famous Francophone personalities",
          "Practice writing simple biographies",
          "Learn regular future tense formation"
        ],
        practiceQuestions: [
          { question: "Who was Father Walter Lini?", answer: "The first Prime Minister of Vanuatu" },
          { question: "What is 'je parlerai'?", answer: "I will speak (future tense)" },
          { question: "How do you say 'He/She was born in...'?", answer: "Il/Elle est né(e) en..." }
        ]
      },
      {
        id: "3.2",
        title: "Mode et traditions mondiales",
        objectives: [
          "Explore global fashion traditions",
          "Compare traditional and modern clothing",
          "Create visual presentations"
        ],
        keyTerms: [
          { term: "Traditionnel", definition: "Traditional" },
          { term: "Moderne", definition: "Modern" },
          { term: "Comparer", definition: "To compare" },
          { term: "Une présentation", definition: "A presentation" }
        ],
        studyNotes: `
## Mode et Traditions Mondiales

### Traditional Clothing Around the World
- **France:** La marinière (striped sailor shirt)
- **Afrique:** Le boubou, le pagne
- **Vanuatu:** La robe mission, le sulu

### Comparing Traditions
- Plus traditionnel que... (More traditional than...)
- Moins moderne que... (Less modern than...)
- Aussi coloré que... (As colorful as...)

### Presentation Vocabulary
- Je vais vous présenter... (I'm going to present to you...)
- Regardez cette image (Look at this image)
- Comme vous pouvez voir... (As you can see...)
- Pour conclure... (To conclude...)
        `,
        examTips: [
          "Use comparatives correctly (plus/moins/aussi...que)",
          "Be able to describe traditional clothing",
          "Practice presentation phrases"
        ],
        practiceQuestions: [
          { question: "What is 'more traditional than'?", answer: "plus traditionnel que" },
          { question: "Name a traditional French clothing item", answer: "la marinière" },
          { question: "How do you start a presentation?", answer: "Je vais vous présenter..." }
        ]
      }
    ]
  },
  strand4: {
    title: "Term 4: Consolidation et projets avancés",
    overview: "Complete media projects and consolidate Year 9 learning for Year 10.",
    image: frenchImages.classroom,
    units: [
      {
        id: "4.1",
        title: "Projet média",
        objectives: [
          "Create multimedia content in French",
          "Use technology for language learning",
          "Present creative work professionally"
        ],
        keyTerms: [
          { term: "Les médias", definition: "Media" },
          { term: "Un podcast", definition: "A podcast" },
          { term: "Une vidéo", definition: "A video" },
          { term: "Créer", definition: "To create" }
        ],
        studyNotes: `
## Projet Média

### Media Project Ideas
1. **Podcast** - Create a short podcast about a topic
2. **Video** - Make a video tour of your school/village
3. **Presentation** - Digital slideshow on a cultural topic
4. **Blog** - Write blog posts in French

### Technical Vocabulary
| French | English |
|--------|---------|
| enregistrer | to record |
| télécharger | to download |
| partager | to share |
| éditer | to edit |

### Podcast Script Structure
1. Introduction: Bonjour et bienvenue à notre podcast!
2. Topic presentation: Aujourd'hui, nous allons parler de...
3. Main content: D'abord... Ensuite... Enfin...
4. Conclusion: Merci d'avoir écouté!
        `,
        examTips: [
          "Plan your media project carefully",
          "Write a script before recording",
          "Practice pronunciation before final recording"
        ],
        practiceQuestions: [
          { question: "What does 'enregistrer' mean?", answer: "to record" },
          { question: "How do you welcome listeners to a podcast?", answer: "Bonjour et bienvenue!" },
          { question: "What transition words can you use?", answer: "D'abord, ensuite, enfin" }
        ]
      },
      {
        id: "4.2",
        title: "Révision avancée",
        objectives: [
          "Consolidate all Year 9 learning",
          "Achieve intermediate proficiency",
          "Prepare for advanced Year 10 content"
        ],
        keyTerms: [
          { term: "Consolider", definition: "To consolidate" },
          { term: "Progresser", definition: "To progress" },
          { term: "Améliorer", definition: "To improve" },
          { term: "Maîtriser", definition: "To master" }
        ],
        studyNotes: `
## Révision Year 9

### Key Topics Covered
1. **Travel:** Vocabulary, Francophone countries
2. **Celebrations:** Festivals, invitations
3. **Fashion:** Clothing, descriptions
4. **History:** Famous figures, biographies
5. **Media:** Podcasts, presentations

### Grammar Mastery
- Simple future tense (je parlerai)
- Comparatives (plus/moins/aussi...que)
- Conditional (je voudrais)
- Past tense review (passé composé)

### Skills Assessment
- Can I discuss travel plans?
- Can I describe clothing and fashion?
- Can I write about famous people?
- Can I create media content in French?
        `,
        examTips: [
          "Review all vocabulary from each term",
          "Practice speaking and writing equally",
          "Identify areas for improvement before Year 10"
        ],
        practiceQuestions: [
          { question: "What tenses have you learned?", answer: "Present, passé composé, future, conditional" },
          { question: "What skills did you develop this year?", answer: "Travel vocabulary, fashion, history, media creation" },
          { question: "What do you want to improve next year?", answer: "Personal reflection" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 10 FRENCH STUDENT NOTES
// ============================================
export const year10FrenchStudentNotes = {
  strand1: {
    title: "Term 1: Identité et voyages",
    overview: "Advanced self-presentation, Francophone diversity, and multimedia creation.",
    image: frenchImages.travel,
    units: [
      {
        id: "1.1",
        title: "Moi, mes origines, mon quotidien",
        objectives: [
          "Present yourself in advanced detail",
          "Form complex questions",
          "Use advanced vocabulary for daily life"
        ],
        keyTerms: [
          { term: "Les origines", definition: "Origins/background" },
          { term: "Le quotidien", definition: "Daily life" },
          { term: "L'identité", definition: "Identity" },
          { term: "Approfondi", definition: "In-depth" }
        ],
        studyNotes: `
## Identité Avancée (Advanced Identity)

### Advanced Self-Introduction
- Je suis originaire de... (I am originally from...)
- Ma famille vient de... (My family comes from...)
- Je me considère comme... (I consider myself as...)

### Complex Questions
- Qu'est-ce qui te passionne? (What are you passionate about?)
- Comment te vois-tu dans 10 ans? (How do you see yourself in 10 years?)
- Quels sont tes projets d'avenir? (What are your future plans?)

### Daily Life in Detail
- Ma routine quotidienne (My daily routine)
- Mes responsabilités (My responsibilities)
- Mes loisirs et passions (My hobbies and passions)
        `,
        examTips: [
          "Go beyond basic introductions",
          "Use complex sentence structures",
          "Express opinions with justification"
        ],
        practiceQuestions: [
          { question: "How do you say 'I am originally from...'?", answer: "Je suis originaire de..." },
          { question: "What does 'le quotidien' mean?", answer: "daily life" },
          { question: "Write 3 complex sentences about yourself", answer: "Personal response with advanced vocabulary" }
        ]
      },
      {
        id: "1.2",
        title: "Voyages et diversité francophone",
        objectives: [
          "Explore Francophone diversity in depth",
          "Create tourist brochures",
          "Make podcasts about travel"
        ],
        keyTerms: [
          { term: "La diversité", definition: "Diversity" },
          { term: "Une brochure", definition: "A brochure" },
          { term: "Un podcast", definition: "A podcast" },
          { term: "Promouvoir", definition: "To promote" }
        ],
        studyNotes: `
## Diversité Francophone

### Francophone Countries Study
- **Europe:** France, Belgique, Suisse
- **Afrique:** Sénégal, Côte d'Ivoire, Madagascar
- **Amériques:** Québec, Haïti, Louisiane
- **Pacifique:** Vanuatu, Nouvelle-Calédonie, Polynésie

### Creating Tourism Materials
- Venez découvrir... (Come discover...)
- Un paradis sur terre (A paradise on earth)
- À ne pas manquer... (Not to be missed...)

### Podcast Creation
- Bienvenue à notre podcast de voyage!
- Aujourd'hui nous visitons...
- Ce qui est unique ici, c'est...
- N'oubliez pas de vous abonner!
        `,
        examTips: [
          "Know detailed facts about Francophone countries",
          "Use persuasive language for tourism",
          "Practice creating audio content"
        ],
        practiceQuestions: [
          { question: "Name Francophone countries on 3 continents", answer: "Europe: France; Africa: Senegal; Pacific: Vanuatu (or others)" },
          { question: "How do you say 'Come discover...'?", answer: "Venez découvrir..." },
          { question: "What phrase means 'Not to be missed'?", answer: "À ne pas manquer" }
        ]
      }
    ]
  },
  strand2: {
    title: "Term 2: Littérature et théâtre",
    overview: "Introduction to French literature, theater, and expressive reading.",
    image: frenchImages.cafe,
    units: [
      {
        id: "2.1",
        title: "La littérature francophone",
        objectives: [
          "Read and analyze French literature",
          "Understand literary vocabulary",
          "Write literary analysis"
        ],
        keyTerms: [
          { term: "La littérature", definition: "Literature" },
          { term: "Un roman", definition: "A novel" },
          { term: "Un poème", definition: "A poem" },
          { term: "L'auteur", definition: "The author" }
        ],
        studyNotes: `
## La Littérature Francophone

### Literary Vocabulary
| French | English |
|--------|---------|
| un roman | a novel |
| une nouvelle | a short story |
| un poème | a poem |
| une pièce de théâtre | a play |
| l'auteur | the author |
| le personnage | the character |

### Famous Francophone Authors
- **Victor Hugo** - Les Misérables
- **Antoine de Saint-Exupéry** - Le Petit Prince
- **Aimé Césaire** - Poète martiniquais

### Literary Analysis
- Le thème principal est... (The main theme is...)
- L'auteur veut montrer... (The author wants to show...)
- Ce passage illustre... (This passage illustrates...)
        `,
        examTips: [
          "Read simplified French texts",
          "Learn to identify themes and messages",
          "Practice expressing opinions about literature"
        ],
        practiceQuestions: [
          { question: "What is 'un roman'?", answer: "a novel" },
          { question: "Who wrote Le Petit Prince?", answer: "Antoine de Saint-Exupéry" },
          { question: "How do you say 'The main theme is...'?", answer: "Le thème principal est..." }
        ]
      },
      {
        id: "2.2",
        title: "Le théâtre",
        objectives: [
          "Learn theater vocabulary",
          "Perform short scenes",
          "Understand theatrical expression"
        ],
        keyTerms: [
          { term: "Le théâtre", definition: "Theater" },
          { term: "Une scène", definition: "A scene" },
          { term: "Un acteur", definition: "An actor" },
          { term: "Jouer un rôle", definition: "To play a role" }
        ],
        studyNotes: `
## Le Théâtre

### Theater Vocabulary
| French | English |
|--------|---------|
| la scène | the stage |
| le public | the audience |
| le rideau | the curtain |
| un acte | an act |
| une réplique | a line (dialogue) |

### Famous French Playwrights
- **Molière** - Le Malade imaginaire
- **Jean Racine** - Andromaque
- **Edmond Rostand** - Cyrano de Bergerac

### Performance Expressions
- Entrez en scène! (Enter the stage!)
- Plus fort! (Louder!)
- Avec expression! (With expression!)
- Bravo! Excellent!
        `,
        examTips: [
          "Practice performing short dialogues",
          "Use appropriate facial expressions",
          "Project your voice clearly"
        ],
        practiceQuestions: [
          { question: "Who was Molière?", answer: "A famous French playwright" },
          { question: "What is 'la scène'?", answer: "the stage" },
          { question: "How do you say 'Louder!' to an actor?", answer: "Plus fort!" }
        ]
      }
    ]
  },
  strand3: {
    title: "Term 3: Mystères et citoyenneté",
    overview: "Explore detective fiction and global citizenship themes.",
    image: frenchImages.eiffel,
    units: [
      {
        id: "3.1",
        title: "L'univers policier",
        objectives: [
          "Learn detective/mystery vocabulary",
          "Analyze crime fiction",
          "Write mystery descriptions"
        ],
        keyTerms: [
          { term: "Un polar", definition: "A detective novel" },
          { term: "L'enquête", definition: "The investigation" },
          { term: "Un indice", definition: "A clue" },
          { term: "Le suspect", definition: "The suspect" }
        ],
        studyNotes: `
## L'Univers Policier (Detective World)

### Detective Vocabulary
| French | English |
|--------|---------|
| un détective | a detective |
| une enquête | an investigation |
| un crime | a crime |
| un indice | a clue |
| un témoin | a witness |
| un suspect | a suspect |

### Famous French Detectives
- **Commissaire Maigret** (Georges Simenon)
- **Arsène Lupin** (Maurice Leblanc)

### Analyzing Mystery Stories
- L'intrigue commence quand... (The plot begins when...)
- Le mystère est résolu par... (The mystery is solved by...)
- Les indices importants sont... (The important clues are...)
        `,
        examTips: [
          "Learn vocabulary for describing crimes and investigations",
          "Practice narrating mystery plots",
          "Use past tense for storytelling"
        ],
        practiceQuestions: [
          { question: "What is 'une enquête'?", answer: "an investigation" },
          { question: "Name a famous French detective", answer: "Commissaire Maigret / Arsène Lupin" },
          { question: "What is 'un indice'?", answer: "a clue" }
        ]
      },
      {
        id: "3.2",
        title: "Citoyen du monde",
        objectives: [
          "Discuss global citizenship",
          "Explore Vanuatu's place in the world",
          "Express opinions on global issues"
        ],
        keyTerms: [
          { term: "Un citoyen", definition: "A citizen" },
          { term: "Le monde", definition: "The world" },
          { term: "La responsabilité", definition: "Responsibility" },
          { term: "L'avenir", definition: "The future" }
        ],
        studyNotes: `
## Citoyen du Monde (Global Citizen)

### Global Citizenship Vocabulary
| French | English |
|--------|---------|
| un citoyen du monde | a global citizen |
| les droits humains | human rights |
| la paix | peace |
| la solidarité | solidarity |

### Vanuatu in the World
- Membre des Nations Unies
- Membre de la Francophonie
- Relations avec les pays du Pacifique
- Défis du changement climatique

### Expressing Global Concerns
- Je pense que... (I think that...)
- Il est important de... (It's important to...)
- Nous devons agir pour... (We must act for...)
- L'avenir dépend de... (The future depends on...)
        `,
        examTips: [
          "Know Vanuatu's international connections",
          "Be able to discuss global issues in French",
          "Use opinion phrases with justification"
        ],
        practiceQuestions: [
          { question: "What does 'citoyen du monde' mean?", answer: "global citizen" },
          { question: "Is Vanuatu a member of La Francophonie?", answer: "Yes" },
          { question: "How do you say 'I think that...'?", answer: "Je pense que..." }
        ]
      }
    ]
  },
  strand4: {
    title: "Term 4: Maîtrise et préparation Senior",
    overview: "Complete final projects and prepare for Senior French studies.",
    image: frenchImages.classroom,
    units: [
      {
        id: "4.1",
        title: "Projet final Junior",
        objectives: [
          "Complete a comprehensive final project",
          "Demonstrate mastery of junior French",
          "Present professionally in French"
        ],
        keyTerms: [
          { term: "Un portfolio", definition: "A portfolio" },
          { term: "La maîtrise", definition: "Mastery" },
          { term: "Démontrer", definition: "To demonstrate" },
          { term: "Évaluer", definition: "To evaluate" }
        ],
        studyNotes: `
## Projet Final Junior

### Project Options
1. **Portfolio** - Collection of your best work
2. **Presentation** - Comprehensive oral presentation
3. **Research project** - In-depth study of a topic
4. **Creative project** - Video, podcast, or performance

### Portfolio Contents
- Written work samples
- Audio/video recordings
- Self-assessment reflections
- Teacher feedback

### Final Presentation Tips
- Parlez clairement (Speak clearly)
- Regardez le public (Look at the audience)
- Utilisez des supports visuels (Use visual aids)
- Répondez aux questions avec confiance (Answer questions confidently)
        `,
        examTips: [
          "Showcase your best work",
          "Practice your presentation multiple times",
          "Be prepared to answer questions"
        ],
        practiceQuestions: [
          { question: "What is 'un portfolio'?", answer: "a portfolio - a collection of your work" },
          { question: "What should you include in your final presentation?", answer: "Visual aids, clear speaking, eye contact" },
          { question: "How do you say 'Speak clearly'?", answer: "Parlez clairement" }
        ]
      },
      {
        id: "4.2",
        title: "Préparation Senior French",
        objectives: [
          "Review all Junior French content",
          "Achieve B1 proficiency level",
          "Prepare for Senior French (Years 11-13)"
        ],
        keyTerms: [
          { term: "La préparation", definition: "Preparation" },
          { term: "Le niveau", definition: "Level" },
          { term: "Senior", definition: "Senior (Years 11-13)" },
          { term: "Continuer", definition: "To continue" }
        ],
        studyNotes: `
## Préparation pour Senior French

### Junior French Summary (Years 7-10)
- **Year 7:** Introductions, family, school
- **Year 8:** Community, traditions, environment
- **Year 9:** Travel, celebrations, history
- **Year 10:** Literature, theater, citizenship

### Skills for Senior French
- Reading comprehension of longer texts
- Writing structured essays
- Oral presentations and debates
- Advanced grammar and vocabulary

### Senior French Topics Preview
- Advanced literature analysis
- Professional French communication
- Cultural studies in depth
- Exam preparation (external exams)

### Self-Assessment Checklist
✓ Can I introduce myself in detail?
✓ Can I discuss various topics fluently?
✓ Can I write paragraphs with proper structure?
✓ Can I understand spoken French at normal speed?
        `,
        examTips: [
          "Identify your strengths and weaknesses",
          "Practice all four skills: reading, writing, listening, speaking",
          "Set goals for Senior French"
        ],
        practiceQuestions: [
          { question: "What year levels are Senior French?", answer: "Years 11, 12, and 13" },
          { question: "What skills will you develop in Senior French?", answer: "Advanced reading, writing, speaking, and listening" },
          { question: "What topics did you cover in Junior French?", answer: "Introductions, community, travel, celebrations, literature, citizenship" }
        ]
      }
    ]
  }
};

export default {
  year7FrenchStudentNotes,
  year8FrenchStudentNotes,
  year9FrenchStudentNotes,
  year10FrenchStudentNotes,
  frenchImages
};
